/* eslint-disable import/prefer-default-export */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useVirtual } from 'react-virtual';

// ASSETS
import { Checkmark } from 'assets';

// COMPONENTS
import { SimpleLoading } from 'components/Loading';
import FleetChargeModal, { DISCOUNTS_VALUES } from './components';

// CONSTANTS
import { CHARGE_RATE_UNITS, ChargeCategory } from 'constants/charges';
import { USER_ROLES } from 'constants/common';

// HOOKS
import { useAppDispatch } from 'hooks/store';
import useCurrency from 'hooks/useCurrency';

// STYLES
import { TableButtonWrapper } from 'styles/buttons';
import { SectionContent, SubSection, SubSectionTitle } from 'styles/cards';
import colors from 'styles/colors';
import {
  CheckmarkContainer,
  LightColoredText,
  RightAligned,
  ChargeCatalogueTableContainer,
  CenteredColumnHeader,
} from './styles';

// SERVICES
import { getCharges } from 'services/FleetSettings/thunks';
// TODO: extract from Usermanagement and import from new generic location
import { selectUser } from 'services/Authentication/selectors';
import { selectCharges, selectTaxes } from 'services/FleetSettings/selectors';
import {
  LoadingState,
  SortableHeader,
  SortingIcon,
  StyledActionButtonsContainer,
  StyledSortableIcon,
} from 'components/SectionTable';

// TYPES
import { DefaultCharge, DefaultChargeResource, DefaultDiscount, DefaultWarranty } from 'services/resources';
import Ellipsing from 'components/Ellipsing/Ellipsing.component';
import { Camelized } from 'humps';
import { selectSelectedHierarchyEntity } from 'services/Hierarchy/selectors';

const useChargeTable = (isDiscount = false) => {
  const selectedHierarchyEntity = useSelector(selectSelectedHierarchyEntity());

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const currencyFormat = useCurrency();
  const [loading, setLoading] = useState(false);

  const currentUser = useSelector(selectUser());
  const charges = useSelector(selectCharges());
  const taxes = useSelector(selectTaxes());
  const [sorting, setSorting] = useState([]);

  const columnHelper = createColumnHelper<DefaultCharge>();

  useEffect(() => {
    let mounted = true;
    const fetchCharges = async () => {
      setLoading(true);
      await dispatch(getCharges());
      if (mounted) {
        setLoading(false);
      }
    };
    fetchCharges();

    return () => {
      mounted = false;
    };
  }, [selectedHierarchyEntity, dispatch]);

  const [chargeData, discountWarrantyData] = useMemo(() => {
    const onlyCharges: DefaultCharge[] = [];
    const onlyWarrantyAndDiscount: (DefaultDiscount | DefaultWarranty)[] = [];

    charges.forEach((charge) => {
      if (DISCOUNTS_VALUES.includes(charge.get('category') ?? ChargeCategory.OTHER)) {
        onlyWarrantyAndDiscount.push(charge.toJS() as DefaultDiscount | DefaultWarranty);
      } else {
        onlyCharges.push(charge.toJS() as DefaultCharge);
      }
    });

    return [onlyCharges, onlyWarrantyAndDiscount];
  }, [charges]);

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor('code', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Charges.code',
        }),
        size: 70,
        cell: (cell) => {
          return <Ellipsing text={cell.getValue()} />;
        },
      }),
      columnHelper.accessor('description', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Charges.item',
        }),
        cell: (cell) => {
          return <Ellipsing text={cell.getValue()} />;
        },
      }),
      columnHelper.accessor('category', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Charges.category',
        }),
        cell: (cell) => {
          return <FormattedMessage id={`views.FleetSettings.Charges.categories.${cell.getValue()}`} />;
        },
        size: 130,
      }),
      columnHelper.accessor('price', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Charges.price',
        }),
        cell: (cell) => {
          const priceValue = cell.getValue();

          let outputValue: string | (string | React.ReactNode)[];

          if (priceValue !== 0) {
            outputValue = currencyFormat.format(priceValue);
          } else {
            const zeroParts = currencyFormat.formatToParts(priceValue);

            const partsValue = zeroParts.map((part) => part.value);

            const currencyIndex = zeroParts.findIndex((part) => part.type === 'currency');

            if (currencyIndex === 0) {
              // Currency is first part
              outputValue = [
                partsValue[currencyIndex],
                <LightColoredText key="zero">{partsValue.slice(currencyIndex + 1).join('')}</LightColoredText>,
              ];
            } else if (currencyIndex === zeroParts.length - 1) {
              // Currency is last part
              outputValue = [
                <LightColoredText key="zero">{partsValue.slice(0, currencyIndex).join('')}</LightColoredText>,
                partsValue[currencyIndex],
              ];
            } else {
              // Currency is in the middle somewhere
              outputValue = [
                <LightColoredText key="zero">{partsValue.slice(0, currencyIndex).join('')}</LightColoredText>,
                partsValue[currencyIndex],
                <LightColoredText key="rest">{partsValue.slice(currencyIndex + 1).join('')}</LightColoredText>,
              ];
            }
          }

          return <RightAligned>{outputValue}</RightAligned>;
        },
        size: 80,
      }),
      columnHelper.accessor('overridable', {
        header: () => (
          <CenteredColumnHeader>
            <FormattedMessage id="views.FleetSettings.Charges.overridable" />
          </CenteredColumnHeader>
        ),
        size: 90,
        cell: (cell) => {
          return cell.getValue() === true ? (
            <CheckmarkContainer>
              <Checkmark />
            </CheckmarkContainer>
          ) : null;
        },
        enableSorting: false,
      }),
      columnHelper.accessor('isDefaultCharge', {
        header: () => (
          <CenteredColumnHeader>
            <FormattedMessage id="views.FleetSettings.Charges.default" />
          </CenteredColumnHeader>
        ),
        size: 85,
        cell: (cell) => {
          return cell.getValue() === true ? (
            <CheckmarkContainer>
              <Checkmark />
            </CheckmarkContainer>
          ) : null;
        },
        enableSorting: false,
      }),

      columnHelper.display({
        id: 'actions',
        header: () => null,
        enableResizing: false,
        size: 48,
        cell: ({ row }) => {
          const charge = row.original;

          return charge ? (
            <StyledActionButtonsContainer>
              {charge.category === 'warranty' || charge.category === 'discount' ? (
                <FleetChargeModal.ChargeButton charge={charge} warAndDisc edit />
              ) : (
                <FleetChargeModal.ChargeButton charge={charge} edit />
              )}
            </StyledActionButtonsContainer>
          ) : null;
        },
      }),
    ];

    if (!isDiscount) {
      columns.splice(
        4,
        0,
        columnHelper.accessor('quantityUnit', {
          header: intl.formatMessage({
            id: 'views.FleetSettings.Charges.unit',
          }),
          cell: (cell) => {
            const value = CHARGE_RATE_UNITS.find((rate) => rate.key === cell.getValue());
            return <FormattedMessage id={value ? value.label.translationId : 'notApplicable'} />;
          },
          size: 106,
        })
      );

      taxes.forEach((globalTax, index) =>
        columns.splice(
          7 + index,
          0,
          // @ts-expect-error Argument of type 'ColumnDef<Camelized<DefaultChargeResource>, Camelized<TaxResource>[]>'...
          columnHelper.accessor((row) => row.taxes?.filter((tax) => tax.id === globalTax.get('id')), {
            id: `tax-${globalTax.get('id')}`,
            header: () => <CenteredColumnHeader>{globalTax.get('code')}</CenteredColumnHeader>,
            cell: (cell) => {
              const hasTax = cell.getValue()?.length > 0;

              return hasTax ? (
                <CheckmarkContainer>
                  <Checkmark />
                </CheckmarkContainer>
              ) : null;
            },
            size: 50,
            maxSize: 50,
            enableSorting: false,
          })
        )
      );

      columns.splice(
        5,
        0,
        columnHelper.accessor('autofill', {
          header: intl.formatMessage({
            id: 'views.FleetSettings.Charges.autofillColumnLabel',
          }),
          cell: (cell) => {
            return cell.getValue() === true ? (
              <CheckmarkContainer>
                <Checkmark />
              </CheckmarkContainer>
            ) : null;
          },
          enableSorting: false,
          size: intl.locale.includes('fr') ? 125 : 70, // fit both translation
        })
      );
    }

    return columns;
  }, [columnHelper, intl, isDiscount, currencyFormat, taxes]);

  const columnVisibility = {
    actions: currentUser.profile.role === USER_ROLES.admin,
  };

  const tableData: Camelized<DefaultChargeResource>[] = isDiscount ? discountWarrantyData : chargeData;

  const chargeTable = useReactTable({
    data: tableData,
    defaultColumn: {
      size: Number.MAX_SAFE_INTEGER,
    },
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return { chargeTable, loading };
};

export const ChargesTable = () => {
  const { chargeTable, loading: chargeLoading } = useChargeTable();

  const chargeTableContainerRef = useRef(null);

  const { rows: chargeRows } = chargeTable.getRowModel();
  const chargesRowVirtualizer = useVirtual({
    parentRef: chargeTableContainerRef,
    size: chargeRows.length,
    overscan: 4,
  });
  const { virtualItems: virtualChargeRows, totalSize: totalChargesSize } = chargesRowVirtualizer;

  const chargesPaddingTop = virtualChargeRows.length > 0 ? virtualChargeRows?.[0]?.start || 0 : 0;
  const chargesPaddingBottom =
    virtualChargeRows.length > 0 ? totalChargesSize - (virtualChargeRows?.[virtualChargeRows.length - 1]?.end || 0) : 0;

  const { chargeTable: discountTable, loading: discountLoading } = useChargeTable(true);

  const warrantyAndDiscountTableContainerRef = useRef(null);

  const { rows: warrantyAndDiscountRows } = discountTable.getRowModel();
  const warrantyAndDiscountsRowVirtualizer = useVirtual({
    parentRef: warrantyAndDiscountTableContainerRef,
    size: warrantyAndDiscountRows.length,
    overscan: 4,
  });
  const { virtualItems: virtualWarrantyAndDiscountRows, totalSize: totalWarrantyAndDiscountsSize } =
    warrantyAndDiscountsRowVirtualizer;

  const warrantyAndDiscountPaddingTop =
    virtualWarrantyAndDiscountRows.length > 0 ? virtualWarrantyAndDiscountRows?.[0]?.start || 0 : 0;
  const warrantyAndDiscountPaddingBottom =
    virtualWarrantyAndDiscountRows.length > 0
      ? totalWarrantyAndDiscountsSize -
        (virtualWarrantyAndDiscountRows?.[virtualWarrantyAndDiscountRows.length - 1]?.end || 0)
      : 0;

  const showSortingIcons = (canSort, isSorted) => {
    if (canSort && !isSorted) {
      return <StyledSortableIcon size={16} color={colors.lightGrayIcon} />;
    }
    if (isSorted) {
      return (
        <SortingIcon
          size={14}
          color={colors.fleetDarkGray}
          direction={
            {
              asc: SortingIcon.Direction.Up,
              desc: SortingIcon.Direction.Down,
            }[isSorted]
          }
        />
      );
    }
    return null;
  };

  return (
    <SectionContent>
      <SubSection>
        <SubSectionTitle>
          <FormattedMessage id="views.FleetSettings.Charges.title" />
        </SubSectionTitle>
        <ChargeCatalogueTableContainer ref={chargeTableContainerRef}>
          {!chargeLoading ? (
            <table className="table">
              <thead className="thead">
                {
                  // Loop over the header rows
                  chargeTable.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isSorted = header.column.getIsSorted();
                        const canSort = header.column.getCanSort();
                        return (
                          <SortableHeader
                            key={header.id}
                            $sortable={canSort}
                            style={{
                              width: header.getSize() === Number.MAX_SAFE_INTEGER ? undefined : header.getSize(),
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {header.isPlaceholder || header.id === 'actions' ? null : (
                              <div>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {showSortingIcons(canSort, isSorted)}
                              </div>
                            )}
                          </SortableHeader>
                        );
                      })}
                    </tr>
                  ))
                }
              </thead>
              <tbody className="tbody">
                {chargesPaddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${chargesPaddingTop}px` }} />
                  </tr>
                )}
                {virtualChargeRows.map((virtualRow) => {
                  const row = chargeRows[virtualRow.index];
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  );
                })}
                {chargesPaddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${chargesPaddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <LoadingState>
              <SimpleLoading color={colors.fleetBlue} />
            </LoadingState>
          )}
        </ChargeCatalogueTableContainer>
        {!chargeLoading && (
          <TableButtonWrapper>
            <FleetChargeModal.ChargeButton />
          </TableButtonWrapper>
        )}
      </SubSection>
      <SubSection>
        <SubSectionTitle>
          <FormattedMessage id="views.FleetSettings.Charges.titleDiscount" />
        </SubSectionTitle>
        <ChargeCatalogueTableContainer ref={warrantyAndDiscountTableContainerRef}>
          {!discountLoading ? (
            <table className="table">
              <thead className="thead">
                {
                  // Loop over the header rows
                  discountTable.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isSorted = header.column.getIsSorted();
                        const canSort = header.column.getCanSort();
                        return (
                          <SortableHeader
                            key={header.id}
                            $sortable={canSort}
                            style={{
                              width: header.getSize() === Number.MAX_SAFE_INTEGER ? undefined : header.getSize(),
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {header.isPlaceholder || header.id === 'actions' ? null : (
                              <div>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {showSortingIcons(canSort, isSorted)}
                              </div>
                            )}
                          </SortableHeader>
                        );
                      })}
                    </tr>
                  ))
                }
              </thead>
              <tbody className="tbody">
                {warrantyAndDiscountPaddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${warrantyAndDiscountPaddingTop}px` }} />
                  </tr>
                )}
                {virtualWarrantyAndDiscountRows.map((virtualRow) => {
                  const row = warrantyAndDiscountRows[virtualRow.index];
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  );
                })}
                {warrantyAndDiscountPaddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${warrantyAndDiscountPaddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <LoadingState>
              <SimpleLoading color={colors.fleetBlue} />
            </LoadingState>
          )}
        </ChargeCatalogueTableContainer>
        {!discountLoading && (
          <TableButtonWrapper>
            <FleetChargeModal.ChargeButton warAndDisc />
          </TableButtonWrapper>
        )}
      </SubSection>
    </SectionContent>
  );
};
